import React, { useState } from 'react';
import { Eye, EyeOff, Shield, Save, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../utils/api';
import { ChangePasswordRequest, UpdateProfileRequest } from '../../types';

export const FirstLoginSetup: React.FC = () => {
  const { user, changePassword, updateProfile, loading, error, clearError, checkAuth, firstLoginSetupComplete, setFirstLoginSetupComplete } = useAuth();
  
  const [passwordData, setPasswordData] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  const [profileData, setProfileData] = useState<UpdateProfileRequest>({
    username: user?.username || '',
    email: user?.email || '',
  });

  // Only update profileData when user changes, not on every render
  React.useEffect(() => {
    setProfileData({
      username: user?.username || '',
      email: user?.email || '',
    });
    // Do NOT reset step here!
    // eslint-disable-next-line
  }, [user?.username, user?.email]);
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Persist step in localStorage for robust state
  const getInitialStep = () => {
    const stored = localStorage.getItem('firstLoginStep');
    console.log('[FirstLoginSetup] getInitialStep - stored:', stored);
    
    // For first-time login, always start with profile step
    // Only use 'password' step if user has explicitly completed profile step
    if (!stored) {
      console.log('[FirstLoginSetup] No stored step, defaulting to profile');
      return 'profile';
    }
    
    if (stored === 'password') {
      console.log('[FirstLoginSetup] Found stored password step, using password');
      return 'password';
    }
    
    console.log('[FirstLoginSetup] Invalid stored step, defaulting to profile');
    return 'profile';
  };
  
  const [step, setStepState] = useState<'profile' | 'password'>(getInitialStep());
  
  const setStep = (newStep: 'profile' | 'password') => {
    console.log('[FirstLoginSetup] setStep called with:', newStep, 'current step:', step);
    setStepState(newStep);
    localStorage.setItem('firstLoginStep', newStep);
    console.log('[FirstLoginSetup] Step updated, localStorage now contains:', localStorage.getItem('firstLoginStep'));
  };
  
  // Clear step from localStorage on setup complete
  React.useEffect(() => {
    if (firstLoginSetupComplete) {
      console.log('[FirstLoginSetup] Setup complete, clearing localStorage');
      localStorage.removeItem('firstLoginStep');
    }
  }, [firstLoginSetupComplete]);
  
  // Reset to profile step on fresh first login
  React.useEffect(() => {
    // Clear any stale localStorage data on fresh first login
    const stored = localStorage.getItem('firstLoginStep');
    const setupComplete = localStorage.getItem('firstLoginSetupComplete');
    
    console.log('[FirstLoginSetup] Initialization check - stored:', stored, 'setupComplete:', setupComplete, 'current step:', step);
    
    // Only reset if setup is not complete and we have invalid stored data
    if (!setupComplete) {
      if (stored && stored !== 'profile' && stored !== 'password') {
        console.log('[FirstLoginSetup] Invalid stored step, clearing and starting with profile');
        localStorage.removeItem('firstLoginStep');
        setStep('profile');
      } else if (!stored && step !== 'profile') {
        console.log('[FirstLoginSetup] No stored step, starting with profile');
        setStep('profile');
      }
    }
  }, []);
  
  // Simple safety check on mount only
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const currentStored = localStorage.getItem('firstLoginStep');
      const currentSetupComplete = localStorage.getItem('firstLoginSetupComplete');
      
      console.log('[FirstLoginSetup] Mount safety check - current step:', step, 'stored:', currentStored, 'setupComplete:', currentSetupComplete);
      
      // Only reset if we're in a clearly invalid state
      if (step === 'password' && !currentSetupComplete && !currentStored) {
        console.log('[FirstLoginSetup] Mount safety check: Invalid password step without stored data, resetting to profile');
        setStep('profile');
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount
  
  const [passwordChanged, setPasswordChanged] = useState(false);

  // DEBUG: Log user and step changes
  React.useEffect(() => {
    console.log('[FirstLoginSetup] user:', user);
  }, [user]);
  React.useEffect(() => {
    console.log('[FirstLoginSetup] step:', step);
  }, [step]);
  
  // Debug: Log what we're about to render
  console.log('[FirstLoginSetup] Rendering with step:', step, 'firstLoginSetupComplete:', firstLoginSetupComplete);
  
  // Early exit if setup is already complete
  if (firstLoginSetupComplete) {
    console.log('[FirstLoginSetup] Setup already complete, exiting');
    return null;
  }
  
  // Monitor firstLoginSetupComplete changes
  React.useEffect(() => {
    console.log('[FirstLoginSetup] firstLoginSetupComplete changed to:', firstLoginSetupComplete);
    if (firstLoginSetupComplete) {
      console.log('[FirstLoginSetup] Setup complete, clearing localStorage');
      localStorage.removeItem('firstLoginStep');
    }
  }, [firstLoginSetupComplete]);
  
  // Handle completion when password is changed
  React.useEffect(() => {
    if (passwordChanged) {
      console.log('[FirstLoginSetup] Password changed, finalizing setup');
      
      // Finalize the setup on the backend
      const finalizeSetup = async () => {
        try {
          await authApi.finalizeDefaultAdminSetup();
          console.log('[FirstLoginSetup] Backend setup finalized');
        } catch (error) {
          console.error('[FirstLoginSetup] Failed to finalize backend setup:', error);
        }
        
        // Set local state
        setFirstLoginSetupComplete(true);
        localStorage.setItem('firstLoginSetupComplete', 'true');
        localStorage.removeItem('firstLoginStep');
        
        // Force a page reload to ensure the state is properly applied
        setTimeout(() => {
          console.log('[FirstLoginSetup] Reloading page to apply completion state');
          window.location.reload();
        }, 500);
      };
      
      finalizeSetup();
    }
  }, [passwordChanged, setFirstLoginSetupComplete]);

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!profileData.username?.trim()) {
      errors.username = 'Username is required';
    } else if (profileData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(profileData.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }
    
    if (!profileData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    } else if (passwordData.new_password === 'admin123') {
      errors.new_password = 'Please choose a different password than the default';
    }
    
    if (!passwordData.confirm_password) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    console.log('[FirstLoginSetup] handleProfileSubmit', profileData);
    if (!validateProfile()) {
      return;
    }
    
    try {
      await updateProfile(profileData);
      console.log('[FirstLoginSetup] profile updated, moving to password step');
      setStep('password');
    } catch (error) {
      // Error is handled by context
      console.log('[FirstLoginSetup] profile update error', error);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    console.log('[FirstLoginSetup] handlePasswordSubmit', passwordData);
    if (!validatePassword()) {
      return;
    }
    
    try {
      await changePassword(passwordData);
      setPasswordChanged(true);
      // Re-check auth to update requiresPasswordChange flag
      await checkAuth();
      console.log('[FirstLoginSetup] password changed, setup complete');
    } catch (error) {
      // Error is handled by context
      console.log('[FirstLoginSetup] password change error', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (step === 'profile') {
      setProfileData(prev => ({ ...prev, [name]: value }));
    } else {
      setPasswordData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };


  
  // Show completion screen if password was changed
  if (passwordChanged) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200 px-4">
        <div className="max-w-md w-full">
          <div className="bg-base-100 rounded-lg shadow-xl p-8 text-center">
            <Shield className="w-12 h-12 mx-auto text-success mb-4" />
            <h1 className="text-2xl font-bold text-success mb-2">Setup Complete!</h1>
            <p className="mb-6">Your admin credentials have been updated.<br />You can now access the dashboard.</p>
            <span className="loading loading-spinner loading-md text-primary"></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-4">
      <div className="max-w-md w-full">
        <div className="bg-base-100 rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-warning/20 p-3 rounded-full">
                <Shield className="w-8 h-8 text-warning" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">Security Setup</h1>
            <p className="text-base-content/70">
              Please update your admin credentials for security
            </p>
          </div>

          {/* Warning Banner */}
          <div className="alert alert-warning mb-6">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <div className="font-semibold">First-time setup required</div>
              <div className="text-sm">
                You're using the default admin account. Please update your profile and change the password.
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Setup Progress</span>
              <span className="text-sm text-base-content/70">
                {step === 'profile' ? '50%' : '100%'}
              </span>
            </div>
            <div className="w-full bg-base-300 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: step === 'profile' ? '50%' : '100%' }}
              ></div>
            </div>
          </div>
          
          <div className="steps steps-horizontal w-full mb-8">
            <div className={`step ${step === 'profile' ? 'step-primary' : step === 'password' ? 'step-success' : ''}`}>
              Profile
            </div>
            <div className={`step ${step === 'password' ? 'step-primary' : ''}`}>
              Password
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-error mb-6">
              <span>{error}</span>
            </div>
          )}

          {/* Profile Setup Form */}
          {step === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold">Update Your Profile</h3>
                <p className="text-sm text-base-content/70">
                  Change your username and email from the defaults
                </p>
              </div>

              {/* Username Field */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Username</span>
                </label>
                <input
                  type="text"
                  name="username"
                  value={profileData.username}
                  onChange={handleInputChange}
                  className={`input input-bordered w-full ${
                    validationErrors.username ? 'input-error' : ''
                  }`}
                  placeholder="Choose a new username"
                  disabled={loading}
                />
                {validationErrors.username && (
                  <label className="label">
                    <span className="label-text-alt text-error">{validationErrors.username}</span>
                  </label>
                )}
              </div>

              {/* Email Field */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Email</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  className={`input input-bordered w-full ${
                    validationErrors.email ? 'input-error' : ''
                  }`}
                  placeholder="Enter your email address"
                  disabled={loading}
                />
                {validationErrors.email && (
                  <label className="label">
                    <span className="label-text-alt text-error">{validationErrors.email}</span>
                  </label>
                )}
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-full ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Updating Profile...
                  </>
                ) : (
                  <>
                    Continue to Password
                  </>
                )}
              </button>
            </form>
          )}

          {/* Password Change Form */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold">Change Your Password</h3>
                <p className="text-sm text-base-content/70">
                  Replace the default password with a secure one
                </p>
              </div>

              {/* Current Password Field */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Current Password</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handleInputChange}
                    className={`input input-bordered w-full pr-12 ${
                      validationErrors.current_password ? 'input-error' : ''
                    }`}
                    placeholder="Enter current password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/60 hover:text-base-content"
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {validationErrors.current_password && (
                  <label className="label">
                    <span className="label-text-alt text-error">{validationErrors.current_password}</span>
                  </label>
                )}
              </div>

              {/* New Password Field */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">New Password</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handleInputChange}
                    className={`input input-bordered w-full pr-12 ${
                      validationErrors.new_password ? 'input-error' : ''
                    }`}
                    placeholder="Enter new secure password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/60 hover:text-base-content"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {validationErrors.new_password && (
                  <label className="label">
                    <span className="label-text-alt text-error">{validationErrors.new_password}</span>
                  </label>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Confirm New Password</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handleInputChange}
                    className={`input input-bordered w-full pr-12 ${
                      validationErrors.confirm_password ? 'input-error' : ''
                    }`}
                    placeholder="Confirm new password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/60 hover:text-base-content"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {validationErrors.confirm_password && (
                  <label className="label">
                    <span className="label-text-alt text-error">{validationErrors.confirm_password}</span>
                  </label>
                )}
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-full ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Complete Setup
                  </>
                )}
              </button>
            </form>
          )}
          
          {/* Fallback for invalid step state */}
          {step !== 'profile' && step !== 'password' && (
            <div className="text-center p-6">
              <div className="alert alert-error">
                <span>Invalid setup state detected. Resetting to profile step...</span>
              </div>
              <button 
                className="btn btn-primary mt-4"
                onClick={() => {
                  console.log('[FirstLoginSetup] Manual reset to profile step');
                  setStep('profile');
                }}
              >
                Continue to Profile Setup
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 